import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store, select } from "@ngrx/store";
import { cloneDeep, isEqual } from "lodash-es";
import { Subscription, distinctUntilChanged, merge } from "rxjs";
import { seToolbarSetRightSlider } from "../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions";
import { SLIDER_OPTIONS } from "../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.state";
import { SearchFormType } from "../../search-page/search-page-type.enum";
import { ServerRootService } from '../../utils/routing/serverroot.service';
import { PositionChangeEvent, TimelineDirective } from "../directive/timeline.directive";
import { seTimelineComponentSetData, seTimelineComponentSetFilters, seTimelineComponentSetReport, seTimelineComponentSetReportFilters, seTimelineComponentSetSelectedItem, seTimelineComponentUpdateData, seTimelineComponentUpdateReport } from "../redux/se-timeline.actions";
import { IRadioEprEventTimeline, MonthData, TimelineFilters, YearMonthsData } from "../se-timeline.models";

@Component({
    selector: 'se-timeline-details',
    templateUrl: './se-timeline-details.component.html',
    styleUrls: ['./se-timeline-details.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, MatDividerModule, MatBadgeModule, MatProgressSpinnerModule, FormsModule, MatTooltipModule, TimelineDirective],
    //se metto onPush flickera tutta la view!! se uso i track con funzioni, escono warning!!!
})
export class SeTimelineDetailsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() formType! : SearchFormType;
    @Input() futureVisits: YearMonthsData[] = [];
    @Input() mode: 'clinical' | 'report' = 'clinical'; // impostato dal parent

    baseVisitWidth : number = 32;// Larghezza in px per ogni 'visita' (icona)
    clinicalHistory : YearMonthsData[] = [];
    clonedClinicalHistory : YearMonthsData[] = [];
    clonedReportHistory: YearMonthsData[] = [];
    convertedBaseUrl : string = '';
    defaultIconName : string = 'Generic_Default';
    filters! : TimelineFilters;
    reportFilters! : TimelineFilters;
    fullYearMonths : MonthData[] = [];
    groupingLimit : number = 5;
    monthSection : number = 0;
    viewGroupedData : YearMonthsData[] = [];
    previousData : any | null = null;
    previousReportData : any | null = null;
    reductions : number[] = [0.25, 0.5, 0.75];
    reportHistory: YearMonthsData[] = [];
    screenWidth : number = 0;
    sectionSize : number = 0;
    singleYear : boolean = false;
    visibleMonths : number = 12;
    updateOnClick: boolean = true;
    _showUpcomingEvents : boolean = false;
    _showSpinner: boolean = false;

    private cachedClinicalData: YearMonthsData[] = [];
    private cachedReportData: YearMonthsData[] = [];
    private dataChangedSubscription?: Subscription;
    private reportChangedSubscription?: Subscription;
    private setFiltersSubscription: Subscription[] = [];
    constructor (
        private cdr: ChangeDetectorRef,
        private serverRootService : ServerRootService,
        private store : Store,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.mode) {
            if (this.mode === 'clinical') {
                this.applyClinicalDataToView(this.cachedClinicalData);
            } else {
                this.applyReportDataToView(this.cachedReportData);
            }
        }

        if (changes?.futureVisits) {
            this.futureVisits = changes.futureVisits.currentValue || [];
            this._showUpcomingEvents = this.futureVisits.length > 0;
        }
    }

    ngOnDestroy() {
        this.setFiltersSubscription?.forEach(el => el.unsubscribe());
        this.dataChangedSubscription?.unsubscribe();
        this.reportChangedSubscription?.unsubscribe();
    }

    listenForStoreEvents() {
        const dataFilterSubs = this.store.pipe(select(seTimelineComponentSetFilters)).subscribe({
            next: (data) => {
                let timelineData = data['seTimeline'];
                if (!!timelineData.clinicalHistory) {
                    this.filters = timelineData.clinicalHistory.filters;
                }
            }, error: (error) => { throw new Error(error); }
        });

        const reportFilterSub = this.store.pipe(select(seTimelineComponentSetReportFilters)).subscribe({
            next: (data) => {
                let timelineData = data['seTimeline'];
                if (!!timelineData.reportHistory) {
                    this.reportFilters = timelineData.reportHistory.filters;
                }
            }, error: (error) => { throw new Error(error); }
        });

        this.setFiltersSubscription.push(dataFilterSubs, reportFilterSub);

        this.dataChangedSubscription = merge(
            this.store.pipe(select(seTimelineComponentSetData)),
            this.store.pipe(select(seTimelineComponentUpdateData)))
            .pipe(distinctUntilChanged((prev, curr) => this.deepEqual(prev['seTimeline']?.clinicalHistory, curr['seTimeline']?.clinicalHistory)))
            .subscribe({
            next: (data) => {
                    this._showSpinner = true;
                    let timelineData = data['seTimeline'];
                    if (!!timelineData.clinicalHistory && this.updateOnClick) {
                        const newData = timelineData.clinicalHistory.data;
                        const newFilters = timelineData.clinicalHistory.filters;
                        this.cachedClinicalData = newData; // metto in cache
                        // se sono in modalità clinical, aggiorno la vista
                        if (this.mode === 'clinical') {
                            if (newFilters) {
                                this.filters = newFilters;
                            }
                            if (JSON.stringify(newData) !== JSON.stringify(this.previousData) && this.previousData !== null) {
                                this.filters = { min: null, max: null, isMonthlyView: false };
                            }
                            this.previousData = newData;
                            this.applyClinicalDataToView(newData);
                        }
                    } this._showSpinner = false;
                }, error: (error) => { throw new Error(error); }
            });


        this.reportChangedSubscription = merge(
            this.store.pipe(select(seTimelineComponentSetReport)),
            this.store.pipe(select(seTimelineComponentUpdateReport)))
            .pipe(distinctUntilChanged((prev, curr) => this.deepEqual(prev['seTimeline']?.reportHistory, curr['seTimeline']?.reportHistory)))
            .subscribe({
            next: (data) => {
                    this._showSpinner = true;
                    let timelineData = data['seTimeline'];
                    if (!!timelineData.reportHistory && this.updateOnClick) {
                        const newReport = timelineData.reportHistory.report;
                        const newFilters = timelineData.reportHistory.filters;
                        this.cachedReportData = newReport; // metto in cache
                        if (this.mode === 'report') {
                            if (newFilters) {
                                this.reportFilters = newFilters;
                            }
                            if (JSON.stringify(newReport) !== JSON.stringify(this.previousReportData) && this.previousReportData !== null) {
                                this.reportFilters = { min: null, max: null, isMonthlyView: false };
                            }
                            this.previousReportData = newReport;
                            this.applyReportDataToView(newReport);
                        }
                    } this._showSpinner = false;
                }, error: (error) => { throw new Error(error); }
            });
    }

    ngOnInit () : void {
        this.listenForStoreEvents();
        this.screenWidth = window.innerWidth;
    }

    @HostListener('window:resize', ['$event'])
    onResize (event : UIEvent) : void {
        const target = event.target as Window;
        if (target.innerWidth !== this.screenWidth) {
            this.screenWidth = target.innerWidth;
            this.mode == 'clinical' ? this.applyClinicalDataToView(this.previousData) : this.applyReportDataToView(this.previousReportData);
        }
    }

    private applyClinicalDataToView(newData: YearMonthsData[]) {
        this.clinicalHistory = newData || [];
        this.clonedClinicalHistory = cloneDeep(this.clinicalHistory);
        this.clonedClinicalHistory.forEach((yearData) => {
            yearData.months = yearData.months.map(this.groupVisitsByDay.bind(this));
        });
        this.previousData = newData;
        this.updateTimelineWith(this.clonedClinicalHistory);
    }

    private applyReportDataToView(newReport: YearMonthsData[]) {
        this.reportHistory = newReport || [];
        this.clonedReportHistory = cloneDeep(this.reportHistory);
        this.clonedReportHistory.forEach((yearData) => {
            yearData.months = yearData.months.map(this.groupVisitsByDay.bind(this));
        });
        this.previousReportData = newReport;
        this.updateTimelineWith(this.clonedReportHistory);
    }
 

    private updateTimelineWith(clonedData: YearMonthsData[]) {
        this._showSpinner = true;
        this.cdr.detectChanges();

        setTimeout(() => {
            this.sectionSize = this.calculateSectionSize(clonedData);
            this.monthSection = this.calculateMonthSectionSize();
            this.viewGroupedData = this.generateFullYearMonths(
                this.calculateTimeline(clonedData)
            );

            this.assignGroupingLimit();
            this.singleYear = this.viewGroupedData.length === 1;

            if (clonedData.length === 1) {
                this.positionVisits(this.viewGroupedData);
            }

            this._showSpinner = false;
            this.cdr.detectChanges();
        }, 100);
    }


    private calculateGroupIcon (visits : IRadioEprEventTimeline[]) : string | undefined {
        const icons = new Set(visits.map(v => v.icon));
        const iconGroups = new Set(visits.map(v => v.iconGroup));
        const iconNames = new Set(visits.map(v => v.iconName));

        if (icons.size > 1) {
            return iconGroups.size === 1 ? [...iconGroups][0] :
                iconNames.size === 1 ? [...iconNames][0] : this.defaultIconName;
        } else {
            // se i nomi delle icone combaciano, uso il primo, se i nomi delle icone di gruppo combaciano, uso il primo, altrimenti il default
            return icons.size === 1 ? [...icons][0] : iconNames.size === 1 ? [...iconNames][0] :
                iconGroups.size === 1 ? [...iconGroups][0] : this.defaultIconName;
        }
    }

    private calculateIcon (visits : IRadioEprEventTimeline[]) : string {
        const iconGroups = new Set(visits.map(v => v.iconGroup));
        const iconNames = new Set(visits.map(v => v.iconName));
        // se i nomi delle icone combaciano, uso il primo, se i nomi delle icone di gruppo combaciano, uso il primo, altrimenti il default
        return iconNames.size === 1 ? [...iconNames][0] :
            iconGroups.size === 1 ? [...iconGroups][0] : this.defaultIconName;
    }

    private groupVisitsByDay (monthData : MonthData) : MonthData {
        const visitsGroupedByDay = monthData.visits.reduce((grouped : Record<string, IRadioEprEventTimeline[]>, visit) => {
            const visitDate = new Date(visit.date).toISOString().split('T')[0];
            (grouped[visitDate] ||= []).push(visit);
            return grouped;
        }, {});

        const totalVisits = Object.values(visitsGroupedByDay)
            .reduce((acc, group) => acc + group.length, 0);

        const groupedVisits = Object.values(visitsGroupedByDay).map(visitsOnSameDay => {
            const firstVisit = visitsOnSameDay[0];
            return {
                ...firstVisit,
                grouped: visitsOnSameDay,
                icon: this.calculateIcon(visitsOnSameDay),
            };
        });

        monthData.count = totalVisits;
        return { ...monthData, visits: groupedVisits, icon: this.calculateIcon(monthData.visits) };
    }

    private deepEqual (prev : any, curr : any) : boolean {
        return isEqual(prev, curr);
    }

    private isNull (filters : TimelineFilters) : boolean {
        return filters.min == null || filters.max == null;
    }

    private assignGroupingLimit () {
        this.viewGroupedData.forEach(year => {
            year.months.forEach(month => {
                month.hide = false;
            });
        });

        let activeFilters = this.mode == 'clinical' ? this.filters : this.reportFilters;
        if (!this.isNull(activeFilters)) {
            this.convertFiltersToMonths(activeFilters);
        } else {
            this.viewGroupedData.forEach(year => {
                year.months.forEach(month => (month.hide = false));
            });
        }

        let dataToCheck = this.mode === 'clinical' ? this.clinicalHistory : this.reportHistory;
        if (dataToCheck.length === 1) {
            this.singleYear = true;
            if (this.visibleMonths > 10) {
                this.groupingLimit = 5;
            } else if (this.visibleMonths <= 2) {
                this.groupingLimit = 20;
            } else {
                this.groupingLimit = 10;
            }
        } else {
            this.singleYear = false;
        }
    }

    private positionVisits (data : YearMonthsData[]) : YearMonthsData[] {
        data.forEach(yearData => {
            yearData.months.forEach(monthData => {
                if (!monthData.grouped && monthData.visits.length !== 0) {
                    const visits = monthData.visits;
                    const sortedVisits = this.sortVisitsByDate(visits!);

                    let previousPosition = 0;
                    let availableSpace = this.sectionSize - 150;
                    monthData.spaceNeeded = Math.max(monthData.spaceNeeded!, this.baseVisitWidth);
                    monthData.availableSpace = this.visibleMonths <= 1 ? availableSpace : availableSpace / this.visibleMonths;
                    sortedVisits.forEach((visit, index) => {
                        const basePosition = this.calculateVisitPosition(new Date(visit.date), monthData.availableSpace!, index);
                        visit.position = index === 0
                            ? basePosition
                            : Math.max(basePosition, previousPosition + 32);
                        previousPosition = visit.position;
                    });
                }
            });
        });
        return data;
    }

    private sortVisitsByDate (visits : IRadioEprEventTimeline[]) : IRadioEprEventTimeline[] {
        return visits.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    private calculateVisitPosition (visitDate : Date, spaceInMonth : number, index : number) : number {
        const dayOfMonth = visitDate.getDate();
        const totalDays = new Date(visitDate.getFullYear(), visitDate.getMonth() + 1, 0).getDate();
        return (spaceInMonth / totalDays) * dayOfMonth - (this.baseVisitWidth / 2);
    }

    private groupConsecutivePairs (data : YearMonthsData[]) {
        for (let i = 0; i < data.length; i++) {
            let yearData = data[i];
            let months = yearData.months;

            for (let j = 0; j < months.length - 1; j += 2) {
                let currentMonth = months[j];
                let nextMonth = months[j + 1];

                if (nextMonth) {
                    currentMonth.visits.push(...nextMonth.visits);
                    for (const visit of nextMonth.visits) {
                        if (visit.grouped) {
                            currentMonth.count! += visit.grouped.length;
                        } else {
                            currentMonth.count! += 1;
                        }
                    }

                    if (!currentMonth.groupedMonths) {
                        currentMonth.groupedMonths = [currentMonth.month];
                    }
                    if (!currentMonth.groupedMonths.includes(nextMonth.month)) {
                        currentMonth.groupedMonths.push(nextMonth.month);
                    }
                    nextMonth.visits = [];
                    nextMonth.spaceNeeded = 0;
                }
            }
        }
    }

    private checkIfVisitsNeedGrouping (data : YearMonthsData, groupingLimit ?: number) : YearMonthsData {
        data.months.forEach(month => {
            month.spaceNeeded = 0;
            if (!!groupingLimit && month.visits.length >= groupingLimit) {
                month.grouped = true;
                month.spaceNeeded += this.baseVisitWidth;
            } else {
                month.grouped = false;
                month.spaceNeeded += this.calculateSpaceNeededByMonth(month);
            }
            month.availableSpace = this.calculateMonthSectionSize();
        });
        return data;
    }

    private calculateSectionSize (clinicalData : YearMonthsData[]) : number {
        // calcolo lo spazio a disposizione per ogni anno
        return window.innerWidth / clinicalData.length;
    }

    private calculateMonthSectionSize () {
        // calcolo lo spazio a disposizione per ogni mese
        return this.sectionSize / 12;
    }

    private calculateSpaceNeededByMonth (data : MonthData) {
        // calcolo lo spazio che servirebbe ad ogni mese a meno di quelli raggruppati
        return data.visits.length * this.baseVisitWidth;
    }

    private checkIfMonthFits (data : YearMonthsData) {
        data.months.forEach(month => {
            if (month.spaceNeeded && month.availableSpace) {

                month.fits = month.spaceNeeded <= month.availableSpace;
            }
        });
    }

    private calculateNewSpaceForMonths (data : YearMonthsData, reductionValue : number) {
        let significantMonths = data.months.length; // n mesi che servono
        let nonSignificantMonths = 12 - significantMonths; // n mesi che non servono
        let newSpaceAvailableEachMonth = (this.monthSection * nonSignificantMonths * reductionValue) / significantMonths;  // spazio in più a disposizione per ogni mese

        data.months.forEach(month => {
            month.availableSpace = this.monthSection + newSpaceAvailableEachMonth;
        });
    }

    private removeSpaceUntilFits (data : YearMonthsData, start : number, max : number) {
        this.checkIfVisitsNeedGrouping(data, this.groupingLimit);
        this.checkIfMonthFits(data);
        if (data.months.some(month => !month.fits)) {
            data.fits = false;
            for (let step = start; step <= max; step += 0.25) {
                this.calculateNewSpaceForMonths(data, step);
                this.checkIfMonthFits(data);
                if (data.months.every(month => month.fits)) {
                    data.fits = true;
                    break;
                }
            }
        } else {
            data.fits = true;
        }
    }

    private calculateTimeline (data : YearMonthsData[]) : YearMonthsData[] {
        data.forEach(year => {
            this.removeSpaceUntilFits(year, 0.25, 0.75);
        });

        while (data.some(year => !year.fits) && this.groupingLimit > 1) {
            this.groupingLimit -= 1;
            data.forEach(year => {
                if (data.length === 1) {
                    this.removeSpaceUntilFits(year, 0.50, 1);
                } else {
                    this.removeSpaceUntilFits(year, 0.25, 0.75);
                }
            });
        }

        if (this.groupingLimit === 1) {
            this.groupingLimit += 1;
            data.forEach(year => {
                let notFitting = data.filter(el => !el.fits);
                this.groupConsecutivePairs(notFitting);
                this.removeSpaceUntilFits(year, 0.25, 0.75);
            });
        }
        this.groupingLimit = 5;
        return data;
    }

    private generateFullYearMonths (yearMonthsDataArray : YearMonthsData[]) : YearMonthsData[] {
        const allMonths = Array.from({ length: 12 }, (_, i) => i + 1); // [1, 2, ..., 12]

        return yearMonthsDataArray.map(yearMonthsData => {
            const fullYearMonthsForYear = allMonths.map(month => {
                const existingMonth = yearMonthsData.months.find(m => m.month === month);
                return existingMonth ? existingMonth : { month, visits: [], spaceNeeded: 0, grouped: false };
            });
            return { ...yearMonthsData, months: fullYearMonthsForYear };
        });
    }

    private convertFiltersToMonths (filters : TimelineFilters) {
        if (filters && this.viewGroupedData.length == 1) {
            const minDate = new Date(filters.min as Date);
            const maxDate = new Date(filters.max as Date);

            if (this.singleYear) {
                this.visibleMonths = 0;
            }

            this.viewGroupedData.forEach(year => {
                year.months.forEach(month => {
                    const monthStart = new Date(Number(year.year), month.month - 1, 1); // Primo giorno del mese (base 0)
                    const monthEnd = new Date(Number(year.year), month.month, 0); // Ultimo giorno del mese (base 1)
                    month.hide = monthEnd <= minDate || monthStart >= maxDate;
                    if (!month.hide) {
                        this.visibleMonths++;
                    }
                });
            });
        } else {
            return;
        }
    }

    _getVisitsUrl (visits : IRadioEprEventTimeline[], groupedMonths : any[], icon : string) {
        let iconToShow : string = '';
        if (!groupedMonths || groupedMonths == undefined || groupedMonths.length === 0) {
            iconToShow = icon;
        } else {
            iconToShow = this.calculateGroupIcon(visits) as string;
        }
        const timelineSiteRoot : string = this.serverRootService.getTimelineSiteRoot();
        const path = timelineSiteRoot + 'images/' + iconToShow + '.svg';
        return path;
    }

    _getUrl (iconName : string) {
        const timelineSiteRoot : string = this.serverRootService.getTimelineSiteRoot();
        const path = timelineSiteRoot + 'images/' + iconName + '.svg';
        return path;
    }

    _changeTooltipPosition (event : PositionChangeEvent) : void {
        const { element, position } = event;
        const viewportWidth = window.innerWidth;
        element.classList.remove('tooltip-visible');

        if ((position.right + 10) > viewportWidth) {
            element.style.right = '0px';
        }

        if (position.left < 0) {
            element.style.left = '0px';
        }

        setTimeout(() => {
            element.classList.add('tooltip-visible');
        }, 50);
    }

    _onItemSelection (visit : any) {
        this.updateOnClick = false;
        this.store.dispatch(seToolbarSetRightSlider({ status: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN }));

        setTimeout(() => {
            this.store.dispatch(seTimelineComponentSetSelectedItem({ formType: this.formType, selectedItem: this.mode == 'clinical' ? visit.id : visit.reportId }));
            this.updateOnClick = true;
        }, 500);
    }

    _onSameDayItemSelection (visit : any) {
        this.updateOnClick = false;
        this.store.dispatch(seToolbarSetRightSlider({ status: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN }));

        setTimeout(() => {
            this.store.dispatch(seTimelineComponentSetSelectedItem({ formType: this.formType, selectedItem: this.mode == 'clinical' ? visit.id : visit.reportId }));
            this.updateOnClick = true;
        }, 500);
    }

    _trackByYear(index: number, item: YearMonthsData): string {
        return `year-${index}-${item.date}`;
    }
}
